import { Button, FormBox } from "@/components/ui-kit"
import { ArrowRightIcon } from "@/components/icons"
import type { LoginFieldErrors, LoginFormValues } from "@/components/auth/login-schemas"
import * as React from "react"

type LoginFormProps = {
  form: LoginFormValues
  fieldErrors: LoginFieldErrors
  submitError: string | null
  isLoading: boolean
  onFieldChange: (field: keyof LoginFormValues, value: string) => void
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  onForgotPassword: () => void
  onRent: () => void
}

export function LoginForm({
  form,
  fieldErrors,
  submitError,
  isLoading,
  onFieldChange,
  onSubmit,
  onForgotPassword,
  onRent,
}: LoginFormProps) {
  const passwordRef = React.useRef<HTMLInputElement>(null)

  const handleEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      passwordRef.current?.focus()
    }
  }

  return (
    <>
      <h1 className="mb-6 text-[28px] leading-[36px] font-bold text-primary sm:mb-8 sm:text-[48px] sm:leading-[52px]">
        Вход в личный кабинет
      </h1>

      <form onSubmit={onSubmit} noValidate className="flex w-full max-w-[541px] flex-col gap-4 sm:gap-5">
        <FormBox
          id="login"
          label="Почта"
          type="email"
          defaultValue={form.email}
          onChange={(event) => onFieldChange("email", event.target.value)}
          autoComplete="username"
          enterKeyHint="next"
          onKeyDown={handleEmailKeyDown}
          className="text-4 h-[60px] leading-[22px] sm:text-[16px] sm:leading-6"
        />
        {fieldErrors.email ? <p className="text-sm text-destructive">{fieldErrors.email}</p> : null}

        <FormBox
          ref={passwordRef}
          id="password"
          label="Пароль"
          type="password"
          defaultValue={form.password}
          onChange={(event) => onFieldChange("password", event.target.value)}
          autoComplete="current-password"
          enterKeyHint="go"
          className="text-4 h-[60px] leading-[22px] sm:text-[16px] sm:leading-6"
        />
        {fieldErrors.password ? (
          <p className="text-sm text-destructive">{fieldErrors.password}</p>
        ) : null}
        {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

        <Button
          variant="link"
          type="button"
          className="block w-fit px-0 py-0 text-sm text-muted-foreground underline-offset-4 hover:underline text-brand sm:text-lg sm:leading-6 text-[16px] sm:text-[18px] leading-[22px]"
          onClick={onForgotPassword}
        >
          Не помню пароль
        </Button>

        <Button
          type="submit"
          disabled={isLoading}
          endIcon={<ArrowRightIcon size={24} />}
          className="h-[48px] text-[16px] leading-[24px] sm:h-[60px] sm:text-[18px]"
        >
          {isLoading ? "Входим..." : "Войти"}
        </Button>
      </form>

      <div className="mt-5 w-full max-w-[541px] sm:mt-10">
        <div className="mb-5 flex items-center gap-6 sm:mb-10">
          <span className="h-px flex-1 bg-muted-foreground" />
          <span className="text-[16px] leading-[22px] text-muted-foreground sm:text-[18px]">или</span>
          <span className="h-px flex-1 bg-muted-foreground" />
        </div>
        <Button
          variant="secondary"
          endIcon={<ArrowRightIcon size={24} />}
          onClick={onRent}
          className="h-[48px] text-[16px] leading-[24px] sm:h-[60px] sm:text-[18px]"
        >
          Арендовать бокс до 36 м²
        </Button>
      </div>
    </>
  )
}
